/**
 * Насколько цвет букв отличим на цвете фона (WCAG 2.1, критерий 1.4.3).
 *
 * Считать это обязательно, а не «на глаз»: человек выбирает краски Блока сам, и ничто
 * не мешает ему поставить светло-серые буквы на белом фоне — на его мониторе они, может
 * быть, ещё читаются, а на чужом или при ярком солнце исчезают совсем. Пользователь этого
 * мессенджера видит плохо, поэтому пара с малым различием не запрещается, но о ней
 * говорится словами до того, как человек её сохранил.
 *
 * Порог 4.5 — обычный текст. Крупные заголовки допускают 3.0, но Блок красится целиком, и
 * мелкий текст внутри него есть всегда, поэтому считаем по строгому порогу.
 */

/** Достаточное различие для обычного текста. */
export const ENOUGH_CONTRAST = 4.5;

/** «#rrggbb» в три доли от нуля до единицы. Не тот вид — ничего: краски просто нет. */
const channelsOf = (colour: string): readonly [number, number, number] | null => {
  if (!/^#[0-9a-f]{6}$/i.test(colour)) return null;
  const value = Number.parseInt(colour.slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
};

/**
 * Яркость цвета так, как её видит глаз. Зелёный вносит вклад впятеро больше синего — это
 * не про красоту, а про устройство зрения, и потому именно эти множители стоят в мере
 * доступности.
 */
const luminanceOf = (colour: string): number | null => {
  const channels = channelsOf(colour);
  if (!channels) return null;
  const [red, green, blue] = channels.map((raw) => {
    const part = raw / 255;
    return part <= 0.04045 ? part / 12.92 : ((part + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
};

/**
 * Во сколько раз светлее светлое. Единица — цвета неотличимы, 21 — чёрное на белом.
 * Неизвестный цвет отвечает `null`, а не единицей: «посчитать не смогли» и «сливается»
 * ведут к разным словам на экране.
 */
export const contrastRatio = (first: string, second: string): number | null => {
  const one = luminanceOf(first);
  const two = luminanceOf(second);
  if (one === null || two === null) return null;
  const lighter = Math.max(one, two);
  const darker = Math.min(one, two);
  return (lighter + 0.05) / (darker + 0.05);
};

/** Хватает ли различия для обычного текста. Посчитать не вышло — не пугаем зря. */
export const enoughContrast = (foreground: string, background: string): boolean => {
  const ratio = contrastRatio(foreground, background);
  return ratio === null || ratio >= ENOUGH_CONTRAST;
};
